import React, { useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'

import { LoadingPlaceholder, Flex } from '@sketch/components'
import InfiniteList, { handleFetchMore } from 'components/InfiniteList'
import EmptyState from 'modules/shares/components/EmptyState'
import LoadingError from '../../components/Layout/LoadingError'
import AspectRatioGrid, { ResponseProp } from 'components/AspectRatioGrid'
import { VersionLink } from 'modules/versioning'
import { Layout } from '../../components/Layout'
import {
  GroupName,
  Group,
  ItemImageWrapper,
  ListGridSeparator,
} from '../../components/Grid'
import { SymbolItem } from './SymbolItem'

import useScrollReset from '../../hooks/useScrollReset'
import { useFlag } from 'containers/flags'
import { useSearchComponentsContext } from '../../context/SearchComponentsContext'
import { useSelectedGroupContext } from '../../context/SelectedGroupContext'
import { useCustomGridContext } from '../../context/CustomGridContext'
import { useGetIdentifiers } from '../../operations/useGetIdentifiers'
import { useTrackTimeInComponentPage } from '../../hooks'
import { useGetSymbols } from '../../operations'

import { groupStyles } from '../../utils'
import { trackEvent } from 'modules/analytics'
import { INSPECTOR_URL_HASH } from 'modules/shares/constants'

import type { SymbolParsed } from '../../types'

/**
 * STYLES
 */
const VersionLinkStyled = styled(VersionLink)(
  ({ theme }) => css`
    /* stylelint-disable-next-line selector-type-no-unknown */
    ${ItemImageWrapper}:hover {
      background-color: ${theme.colors.background.tertiary.C};
    }
  `
)

const SymbolsList = ({
  searchValue,
  path,
}: {
  searchValue: string
  path: string | null
}) => {
  const prevSearchRef = useRef<string | undefined>()
  const isCustomGridEnabled = useFlag('cwv-custom-grid')
  const {
    defaultColumns,
    verticalRowSpaceGrid,
    minColumnWidth,
  } = useCustomGridContext()

  useScrollReset()

  const {
    shareIdentifier,
    versionIdentifier,
    entries,
    after,
    loading,
    fetchMore,
    error,
    hasComponentManifest,
  } = useGetSymbols({
    search: searchValue,
    path: path === 'View All' ? null : path,
  })

  useEffect(() => {
    if (searchValue) {
      trackEvent('CWV - search', { type: 'symbols' })
    }
  }, [searchValue])

  useEffect(() => {
    // only update the searchValue after it has finished loading
    if (!loading) {
      prevSearchRef.current = searchValue
    }
  }, [searchValue, loading])

  if (error) {
    return <LoadingError error={error} componentType="Symbols" />
  }

  const switchingGroupToSearch =
    (path !== 'View All' || path === null) && searchValue

  const isLoadingNewSearch =
    loading &&
    searchValue &&
    (prevSearchRef.current === undefined ||
      prevSearchRef.current !== searchValue)

  if (
    (loading && !after) ||
    switchingGroupToSearch ||
    isLoadingNewSearch ||
    !versionIdentifier
  ) {
    return (
      <Flex justifyContent="center" flex="auto">
        <LoadingPlaceholder size="64px" />
      </Flex>
    )
  }

  if (!hasComponentManifest) {
    return (
      <EmptyState
        title="Components aren’t available for this version"
        description="Try saving your document again."
      />
    )
  }

  const symbolsGroups = groupStyles(entries)
  const groupsNames = Object.keys(symbolsGroups).sort()

  if (groupsNames.length === 0) {
    if (searchValue !== '') {
      return (
        <EmptyState
          title={`No search results for "${searchValue}"`}
          description="No search results."
          icon="search"
        />
      )
    }

    if (path !== '' && path !== 'View All') {
      return (
        <EmptyState
          title={`The group "${path}" does not exist in this version.`}
          description=""
          icon="symbol"
        />
      )
    }

    return (
      <EmptyState
        title="No symbols"
        description={
          <>
            This Document does not contain any{' '}
            <a href="https://www.sketch.com/docs/designing/symbols/">Symbols</a>
            .
          </>
        }
        icon="symbol"
      />
    )
  }

  const generateSymbolList = (symbol: SymbolParsed, groupName: string) => (
    <VersionLinkStyled
      key={`${symbol.identifier}-${versionIdentifier}`}
      onClick={() => {
        if (searchValue) {
          trackEvent('CWV - search click', { type: 'symbols' })
        }
      }}
      to={{
        routeKey: 'ARTBOARD_DETAIL',
        routeParams: {
          shareID: shareIdentifier,
          permanentArtboardShortId: symbol.artboard.permanentArtboardShortId!,
        },
        state: { groupId: groupName, symbolName: symbol.name },
        hash: INSPECTOR_URL_HASH,
      }}
    >
      <SymbolItem
        key={`${symbol.identifier}-${versionIdentifier}`}
        symbol={symbol}
        isSearch={searchValue !== ''}
        groupName={groupName}
        searchValue={searchValue}
      />
    </VersionLinkStyled>
  )

  // The main difference between the search view and the normal view is that we
  // don't group items when searching, that's why we need to tweak the list generation
  if (searchValue !== '') {
    const columnsOnSearch = isCustomGridEnabled
      ? defaultColumns
      : // This `splice` modifies the last 2 values replacing them for 4 and 5
        ((defaultColumns as number[]).splice(-3, 3, 3, 4, 5) as ResponseProp)

    return (
      <InfiniteList
        canLoadMore={after !== null}
        onLoadMore={handleFetchMore(
          fetchMore,
          ['share', 'version', 'document', 'components', 'entries'],
          { after: after || null }
        )}
      >
        <AspectRatioGrid
          verticalRowSpace={verticalRowSpaceGrid}
          columns={columnsOnSearch}
          gutterSize={32}
          minWidth={minColumnWidth}
        >
          {groupsNames.map(groupName =>
            symbolsGroups[groupName].map((symbol: SymbolParsed) =>
              generateSymbolList(symbol, groupName)
            )
          )}
        </AspectRatioGrid>
      </InfiniteList>
    )
  }

  return (
    <InfiniteList
      canLoadMore={after !== null}
      onLoadMore={handleFetchMore(
        fetchMore,
        ['share', 'version', 'document', 'components', 'entries'],
        { after: after || null }
      )}
    >
      {groupsNames.map((groupName, key) => (
        <Group key={`${groupName}-${versionIdentifier}`}>
          {key !== 0 && <ListGridSeparator />}
          {groupName !== ' ' && <GroupName groups={groupName} />}
          <AspectRatioGrid
            minWidth={minColumnWidth}
            verticalRowSpace={verticalRowSpaceGrid}
            columns={defaultColumns}
            gutterSize={32}
          >
            {symbolsGroups[groupName].map((symbol: SymbolParsed) =>
              generateSymbolList(symbol, groupName)
            )}
          </AspectRatioGrid>
        </Group>
      ))}
    </InfiniteList>
  )
}

/**
 * MAIN COMPONENT
 */
export const Symbols: React.FC = () => {
  const { search } = useSearchComponentsContext()
  const { selected, setSelected } = useSelectedGroupContext()
  const { group } = useGetIdentifiers()

  useTrackTimeInComponentPage('symbols')

  if (search) {
    setSelected('View All')
  }

  const path = selected ? selected : group

  return (
    <Layout>
      <SymbolsList searchValue={search} path={path} />
    </Layout>
  )
}
